import {
  IMultipleRowActions,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { endOfDay, format, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getPhysicianDisplay,
  IExamManagementNotification,
} from "../../../../models/exam-management-notification-log";
import MainApi from "../../../../services/mainApi.service";
import { reprocessExamManagementNotifications } from "../../../../services/examManagementNotification.service";
import ExamManagementNotificationDetailsModal from "./Details";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const route =
  process.env.REACT_APP_EXTERNAL + "/api/v1/ExamManagementNotification/Search";
const service = new MainApi<IExamManagementNotification>(route);

const initialFilters: IFilterParams = {
  CreatedStart: null,
  CreatedEnd: null,
  Status: "",
  PhysicianIdentifier: "",
  PatientEncounterNumber: "",
};

const statusConfig: Record<
  string,
  { label: string; color: "warning" | "success" | "error" | "info" }
> = {
  PENDING: { label: "Pendente", color: "warning" },
  SENT: { label: "Enviado", color: "success" },
  ERROR: { label: "Erro", color: "error" },
  PROCESSING: { label: "Processando", color: "info" },
};

const ExamManagementNotificationLog = () => {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const [filters, setFilters] = useState<IFilterParams>(initialFilters);
  const [selectedRow, setSelectedRow] =
    useState<IExamManagementNotification | null>(null);
  const { setMessage } = useNotificationContext();
  const { sendRequest: reprocessRequest } = useFetch(
    reprocessExamManagementNotifications,
  );

  const formatDate = useCallback((value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "-"
      : format(date, "Pp", { locale: ptBR }).replaceAll(",", "");
  }, []);

  const columns = useMemo<GridColDef<IExamManagementNotification>[]>(
    () => [
      {
        field: "created",
        headerName: "Data de criação",
        minWidth: 170,
        flex: 1,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "patientEncounterNumber",
        headerName: "Nº Atendimento",
        minWidth: 140,
        flex: 0.8,
        renderCell: ({ value }) => value ?? "-",
      },
      {
        field: "physicianIdentifier",
        headerName: "Médico",
        minWidth: 150,
        flex: 1,
        renderCell: ({ row }) => getPhysicianDisplay(row),
      },
      {
        field: "reportLiberationDate",
        headerName: "Liberação do laudo",
        minWidth: 180,
        flex: 1,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "sentDate",
        headerName: "Data de envio",
        minWidth: 170,
        flex: 1,
        renderCell: ({ value }) => formatDate(value),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 120,
        flex: 0.7,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => {
          const config = statusConfig[value] ?? {
            label: value || "-",
            color: "info" as const,
          };
          return (
            <Chip size="small" label={config.label} color={config.color} />
          );
        },
      },
    ],
    [formatDate],
  );

  const serviceProps = useMemo(
    () => ({
      refresh,
      CreatedStart: filters.CreatedStart
        ? startOfDay(filters.CreatedStart).toISOString()
        : undefined,
      CreatedEnd: filters.CreatedEnd
        ? endOfDay(filters.CreatedEnd).toISOString()
        : undefined,
      Status: filters.Status || undefined,
      PhysicianIdentifier: filters.PhysicianIdentifier || undefined,
      PatientEncounterNumber: filters.PatientEncounterNumber || undefined,
    }),
    [filters, refresh],
  );

  const handleFilter = useCallback((value: IFilterParams) => {
    setFilters(value);
    setRefresh((current) => current + 1);
  }, []);

  const handleReprocess = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;

      const { success } = await reprocessRequest({ ids });
      if (success) {
        setRefresh((current) => current + 1);
        setMessage({
          message:
            ids.length === 1
              ? "Notificação enviada para reprocessamento."
              : "Notificações enviadas para reprocessamento.",
          type: "success",
        });
      }
    },
    [reprocessRequest, setMessage],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids) =>
          handleReprocess(ids.map((id) => String(id))),
        icon: <ReplayIcon fontSize="small" />,
        text: "Reprocessar selecionados",
      },
    ],
    [handleReprocess],
  );

  return (
    <>
      <ExamManagementNotificationDetailsModal
        notification={selectedRow}
        onClose={() => setSelectedRow(null)}
      />
      <Table<IExamManagementNotification>
        title={t(
          "components.menu.4ubus.logs.examManagementNotificationLog",
          "Email Notificações Laudos",
        )}
        columns={columns}
        service={service}
        serviceProps={serviceProps}
        searchInputPlaceHolder="Pesquisar notificações"
        showButton={false}
        showDeleteButton={false}
        showDefaultMacroFilters={false}
        defaultActions
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => row.id}
        macroFilters={useCallback(
          () => (
            <MacroFilter value={filters} onFilter={handleFilter} />
          ),
          [filters, handleFilter],
        )}
        rowActions={(params) => [
          {
            action: () => setSelectedRow(params.row),
            icon: <VisibilityIcon fontSize="small" />,
            text: "Ver detalhes",
          },
          params.row.status === "ERROR"
            ? {
                action: () => handleReprocess([params.row.id]),
                icon: <ReplayIcon fontSize="small" />,
                text: "Reprocessar",
              }
            : null,
        ]}
      />
    </>
  );
};

export default ExamManagementNotificationLog;
